import { useMemo, useState } from 'react'
import dayjs from 'dayjs'

import Avatar from '@/elements/Avatar'
import FormTextArea from '@/elements/FormTextArea'
import Text from '@/elements/Text'
import Flex from '@/elements/Flex'
import { postAlertComment, useGetAlertComments } from '@/api/alert'
import useAuthStore from '@/stores/authStore'
import useBreakpoints from '@/hooks/useBreakpoints'
import Button from '@/elements/Button'
import Loading from '@/elements/Loading'
import type { AlertActivityType, AlertResponse } from '@/api/types/Alert'
import type { CommentResponse } from '@/api/types/Comment'

import AlertActivity from './AlertActivity'
import AlertComment from './AlertComment'

function AlertComments({
  alertId,
  activity = [],
  disabled,
}: {
  alertId: string
  activity: AlertActivityType[]
  disabled?: boolean
}) {
  const { user } = useAuthStore()
  const {
    data: commentsData,
    isLoading: commentsLoading,
    refetch: refetchComments
  } = useGetAlertComments(alertId)
  const { isMobile } = useBreakpoints()

  const [comment, setComment] = useState('')

  const mergedActivity: ({
    type: 'activity'
    createdAt: AlertResponse['createdAt']
    activity: AlertActivityType
  } | {
    type: 'comment'
    createdAt: AlertResponse['createdAt']
    comment: CommentResponse
  })[] = useMemo(() => {
    const organizedActivity = (activity || []).map(item => ({
      type: 'activity' as const,
      createdAt: item.createdAt,
      activity: item
    }))

    const organizedComments = (commentsData?.data || []).map(item => ({
      type: 'comment' as const,
      createdAt: item.createdAt,
      comment: item
    }))

    return [...organizedActivity, ...organizedComments].sort(
      (a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix()
    )
  }, [activity, commentsData?.data])

  const submitComment = async () => {
    if (!alertId || !comment.length) return
    await postAlertComment(alertId, comment)
    refetchComments()
    setComment('')
  }

  return (
    <>
      {!disabled && (
        <Flex wfull align='flex-end' gap={2}>
          <Flex wfull inline align='center' gap={2}>
            {!isMobile && (
              <Avatar
                size='small'
                name={user?.details.fullName || ''}
                src={user?.assets.PROFILE_PICTURE || ''}
              />
            )}
            <FormTextArea
              name='comment'
              placeholder='Add your comment here...'
              disabled={!alertId || commentsLoading}
              value={comment}
              rows={1}
              onChange={e => setComment(e.target.value)}
              style={{ flexGrow: 1 }}
            />
          </Flex>
          <Flex className='form-controls'>
            <Button
              variant='secondary'
              onClick={() => setComment('')}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={submitComment}
              disabled={!comment.length || commentsLoading}
            >
              Publish
            </Button>
          </Flex>
        </Flex>
      )}
      <Flex wfull gap={4} style={{ maxHeight: 320 }} scroll='vertical'>
        {commentsLoading ? (
          <Flex wfull inline justify='center'>
            <Loading />
          </Flex>
        ) : mergedActivity.length ? mergedActivity.map(item => {
          if (item.type === 'activity') {
            return (
              <AlertActivity
                key={item.activity.id}
                activity={item.activity}
              />
            )
          }
          if (item.type === 'comment') {
            return (
              <AlertComment
                key={item.comment.id}
                comment={item.comment}
              />
            )
          }
          return null
        }) : (
          <Text color='black-light-60'>No activity yet.</Text>
        )}
      </Flex>
    </>
  )
}

export default AlertComments
